import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Consultation } from 'src/app/utils/interfaces/new/consultation.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { VisualisationConsultation } from 'src/app/utils/interfaces/new/visualisation-consultation.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ConsultationService {

  private readonly defaultHttpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };
  private uri = environment.basePath + "/consultation/consultations";
  private dirtyForm_List: any[] = [];

  constructor(
    private http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async create(consultation: Consultation): Promise<string> {
    try {
      consultation.date = this.dateIsBeforeActualDate(consultation.date)
      const res = await this.http.post(this.uri, consultation, this.defaultHttpOptions).toPromise();
      const id = res.body as string;

      ConsultationsCache.clearConsultationsCacheForPatient(consultation.idPatient);

      return id;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }


    
  dateIsBeforeActualDate(date: Date): Date {
    try {
      const momentDate = moment(date);
      const today = moment().startOf('day');
      const now = moment();
  
      if (momentDate.isBefore(today)) {
        return momentDate.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toDate();
      } else if (momentDate.isSame(today, 'day')) {
        return now.toDate();
      } else {
        return date;
      }
    } catch (error) {
      console.error('Error in dateIsBeforeActualDate:', error);
      return date;
    }
  }

  public async find(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'date',
    sortOrder: SortEnum.DESC
  }): Promise<Paginated<Consultation>> {
    try {
      const consultationsCache = ConsultationsCache.getConsultationsFromCache(patientId, filtre);
      if (consultationsCache) {
        return consultationsCache;
      }

      let httpOptions = {...this.defaultHttpOptions};
      httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this.http.get(this.uri + "/" + patientId, httpOptions).toPromise();
      const consultations = res.body as Consultation[];
      const paginated: Paginated<Consultation> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: consultations
      };

      ConsultationsCache.setConsultationsToCache(patientId, filtre, paginated);
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async update(consultation: Consultation) {
    try {
      consultation.date = this.dateIsBeforeActualDate(consultation.date)
      const res = await this.http.put(this.uri + "/infos/" + consultation.id, consultation, this.defaultHttpOptions).toPromise();
      ConsultationsCache.clearConsultationsCacheForPatient(consultation.idPatient);
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOne(consultId: string): Promise<Consultation> {
    try {
      const res = await this.http.get(this.uri + "/infos/" + consultId, this.defaultHttpOptions).toPromise();
      const consultation = res.body as Consultation;
      return consultation;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async visualisation(consultationsVisualisation: VisualisationConsultation[]): Promise<string> {
    try {
      const res = await this.http.post(this.uri + "/visualisation", consultationsVisualisation, this.defaultHttpOptions).toPromise();
      const id = res.body as string;
      return id;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getConsultationStats(consultId: string): Promise<ConsultationStats> {
    try {
      const res = await this.http.get<ConsultationStats>(`${environment.basePath}/consultation/stats/${consultId}`, this.defaultHttpOptions).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public addOrUpdateDirtyForm(formId: string, isDirty: boolean): void {
    const existingForm = this.dirtyForm_List.find(form => form.formId === formId);
    if (existingForm) {
      existingForm.dirty = isDirty;
    } else {
      this.dirtyForm_List.push({ formId, dirty: isDirty });
    }
  }

  public removeDirtyForm(formId: string): void {
    this.dirtyForm_List = this.dirtyForm_List.filter(form => form.formId !== formId);
  }

  public isFormDirty(formId: string): boolean {
    const form = this.dirtyForm_List.find(form => form.formId === formId);
    return form ? form.dirty : false;
  }

  public isAnyFormDirty(): boolean {
    return this.dirtyForm_List.some(form => form.dirty);
  }
  
  public ResetFormDirty() {
    this.dirtyForm_List.forEach(form => {
      form.dirty = false; // Réinitialise l'état de chaque formulaire
    });
  }
}

export class ConsultationsCache {

  private static _consultationsMap = new Map<string, Map<string, Paginated<Consultation>>>();

  private static _getConsultationKey(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'date',
    sortOrder: SortEnum.DESC
  }): string {
    return patientId + JSON.stringify(filtre);
  }

  static getConsultationsFromCache(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'date',
    sortOrder: SortEnum.DESC
  }): Paginated<Consultation> | undefined {
    const consultations = this._consultationsMap.get(patientId);
    if (consultations) {
      return consultations.get(this._getConsultationKey(patientId, filtre));
    } else {
      return undefined;
    }
  }
  
  static setConsultationsToCache(patientId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'date',
    sortOrder: SortEnum.DESC
  }, paginatedConsultations: Paginated<Consultation>): void {
    let consultations = this._consultationsMap.get(patientId);
    if (!consultations) {
      consultations = new Map<string, Paginated<Consultation>>();
      this._consultationsMap.set(patientId, consultations);
    }
    consultations.set(this._getConsultationKey(patientId, filtre), paginatedConsultations);
  }

  static clearConsultationsCacheForPatient(patientId: string): void {
    this._consultationsMap.delete(patientId);
  }

  static clearConsultationsCache(): void {
    this._consultationsMap.clear();
  }
}

export interface ConsultationStats {
  qtyImageries?: number;
  qtyCourriers?: number;
  imageries?: Array<string>;
  courriers?: Array<string>;
}

